'use client';

import { useState } from 'react';
import { ArrowDown, ArrowUp, FileText, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@kit/ui/alert-dialog';
import { Button } from '@kit/ui/button';
import { Dialog, DialogContent } from '@kit/ui/dialog';
import { Skeleton } from '@kit/ui/skeleton';
import { Trans } from '@kit/ui/trans';

import { useDocumentEdit } from '../hooks/use-document-edit';
import { useDocumentReorder } from '../hooks/use-document-reorder';
import type {
  DocumentReorderItem,
  DocumentTypeOption,
  DocumentUpdateInput,
  DocumentUploadInput,
  EditableDocumentWithUrl,
} from '../types';
import { DocumentEditDialog } from './document-edit-dialog';
import { DocumentUploadDialog } from './document-upload-dialog';
import { EditableDocumentCard } from './editable-document-card';

interface SortableDocumentListProps<T extends EditableDocumentWithUrl> {
  documents: T[];
  onUpdate?: (documentId: string, data: DocumentUpdateInput) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  onDelete?: (documentId: string) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  onReorder?: (items: DocumentReorderItem[]) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  onUpload?: (input: DocumentUploadInput) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  isLoading?: boolean;
  showUploadButton?: boolean;
  documentTypes?: DocumentTypeOption[];
  acceptedFileTypes?: string;
  emptyMessage?: string;
  className?: string;
  renderBadge?: (document: T) => React.ReactNode;
}

/**
 * Sortable document list with edit, delete, reorder, and upload capabilities
 *
 * @example
 * ```tsx
 * <SortableDocumentList
 *   documents={documents}
 *   onUpdate={async (id, data) => {
 *     await updateDocument({ documentId: id, data });
 *   }}
 *   onDelete={async (id) => {
 *     await deleteDocument(id);
 *   }}
 *   onReorder={async (items) => {
 *     await reorderDocuments(items);
 *   }}
 *   onUpload={async (input) => {
 *     await uploadDocument(input);
 *   }}
 *   documentTypes={[
 *     { value: 'photo', label: 'Photo' },
 *     { value: 'video', label: 'Video' },
 *   ]}
 * />
 * ```
 */
export function SortableDocumentList<T extends EditableDocumentWithUrl>({
  documents,
  onUpdate,
  onDelete,
  onReorder,
  onUpload,
  isLoading = false,
  showUploadButton = true,
  documentTypes,
  acceptedFileTypes,
  emptyMessage,
  className,
  renderBadge,
}: SortableDocumentListProps<T>) {
  const { t } = useTranslation();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Edit hook
  const {
    isEditing,
    editTitle,
    editDescription,
    setEditTitle,
    setEditDescription,
    openEdit,
    closeEdit,
    saveEdit,
    isPending: isEditPending,
    canSave,
  } = useDocumentEdit<T>({
    onUpdate: onUpdate || (async () => {}),
  });

  // Reorder hook
  const {
    sortedDocuments,
    isReordering,
    moveUp,
    moveDown,
    canMoveUp,
    canMoveDown,
  } = useDocumentReorder<T>({
    documents,
    onReorder: onReorder || (async () => {}),
  });

  const handleDelete = async () => {
    if (!deleteDocumentId || !onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(deleteDocumentId);
      setDeleteDocumentId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="text-muted-foreground/50 mb-4 h-12 w-12" />
        <p className="text-muted-foreground mb-4">
          {emptyMessage || (
            <Trans
              i18nKey="documents:list.empty"
              defaults="No documents uploaded yet"
            />
          )}
        </p>
        {showUploadButton && onUpload && (
          <Button onClick={() => setUploadOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            <Trans
              i18nKey="documents:actions.upload"
              defaults="Upload Document"
            />
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={className}>
        {/* Header with upload button */}
        {showUploadButton && onUpload && (
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setUploadOpen(true)} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              <Trans i18nKey="documents:actions.add" defaults="Add Document" />
            </Button>
          </div>
        )}

        {/* Document grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sortedDocuments.map((doc, index) => (
            <div key={doc.id} className="relative">
              {/* Reorder buttons */}
              {onReorder && (
                <div className="absolute top-2 left-2 z-10 flex flex-col gap-0.5">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveUp(index)}
                    disabled={!canMoveUp(index)}
                  >
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveDown(index)}
                    disabled={!canMoveDown(index)}
                  >
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <EditableDocumentCard
                document={doc}
                onView={() => setPreviewUrl(doc.signedUrl)}
                onEdit={onUpdate ? () => openEdit(doc) : undefined}
                onDelete={
                  onDelete ? () => setDeleteDocumentId(doc.id) : undefined
                }
                isReordering={isReordering}
                renderBadge={renderBadge}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl border-none bg-black p-0">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
            onClick={() => setPreviewUrl(null)}
          >
            <X className="h-5 w-5" />
          </Button>
          {previewUrl && (
            /* eslint-disable-next-line @next/next/no-img-element, ballee-media/no-untransformed-media -- blob:/signed document preview, not a public R2 asset; cdnImageUrl would pass it through unchanged. */
            <img
              src={previewUrl}
              alt={t('documents:preview.alt')}
              className="max-h-[90vh] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <DocumentEditDialog
        open={isEditing}
        onOpenChange={(open) => {
          if (!open) closeEdit();
        }}
        title={editTitle}
        description={editDescription}
        onTitleChange={setEditTitle}
        onDescriptionChange={setEditDescription}
        onSave={saveEdit}
        isPending={isEditPending}
        canSave={canSave}
      />

      {/* Upload Dialog */}
      {onUpload && (
        <DocumentUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUpload={onUpload}
          documentTypes={documentTypes}
          acceptedFileTypes={acceptedFileTypes}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteDocumentId}
        onOpenChange={() => setDeleteDocumentId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans
                i18nKey="documents:deleteDialog.title"
                defaults="Delete Document"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="documents:deleteDialog.description"
                defaults="Are you sure you want to delete this document? This action cannot be undone."
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              <Trans i18nKey="common:cancel" defaults="Cancel" />
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trans i18nKey="common:delete" defaults="Delete" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
